import * as React from 'react'
import * as formik from 'formik'
import * as yup from 'yup'
import styled from 'styled-components'

import * as ds from '@ulule/owl-kit-components/next'
import { COLORS, styles as S } from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import { t } from '@owl-nest/localize'
import * as api from '@owl-nest/api-client/latest'

type CommentFormProps = {
  user: api.AuthenticatedUser
  onSubmit: (values: CommentFormValue) => Promise<void>
  onCancel?: () => void
  submitLabel?: React.ReactNode
  autoFocus?: boolean
  id?: string
}

export type CommentFormValue = {
  comment: string
}

const schema = yup.object().shape({
  comment: yup.string().max(500).required(),
})

export function CommentForm({
  user,
  onSubmit,
  onCancel,
  submitLabel,
  autoFocus = false,
}: CommentFormProps): React.ReactElement<CommentFormProps> {
  const initialValues = {
    comment: '',
  }

  const [isOpen, setIsOpen] = React.useState(false)

  const textareaResetRef = React.useRef<() => void>(null)

  return (
    <formik.Formik<CommentFormValue>
      initialValues={initialValues}
      validate={validate}
      onSubmit={submit}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {(formikBag) => {
        return (
          <Wrapper>
            <S.image.Avatar type="medium" src={model.user.avatar(user, '128x128')}></S.image.Avatar>
            <Form onSubmit={formikBag.handleSubmit}>
              <formik.Field name="comment">
                {({ field, meta }: formik.FieldProps<CommentFormValue['comment'], CommentFormValue>) => {
                  return (
                    <ds.TextAreaField
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus={autoFocus}
                      placeholder={t('Your comment')}
                      name={field.name}
                      error={meta.error}
                      onChange={field.onChange}
                      value={field.value}
                      maxLength={500}
                      resetRef={textareaResetRef}
                    />
                  )
                }}
              </formik.Field>
              <S.button.Button>{submitLabel}</S.button.Button>
              {onCancel && !formikBag.isSubmitting && (
                <ds.LinkAsButton kind="secondary" type="button" onClick={onCancel}>
                  {t('Cancel')}
                </ds.LinkAsButton>
              )}
              <S.copy.S>
                {t('Comments are public, we invite you to be careful.')}{' '}
                <PrivacyToggle kind="primary" type="button" onClick={() => setIsOpen(!isOpen)}>
                  {t('Learn why')}.
                </PrivacyToggle>
              </S.copy.S>
              {isOpen && (
                <>
                  <S.copy.S>{t('IMPORTANT: Through your comment, you could:')}</S.copy.S>
                  <S.copy.S>
                    {t(
                      'reveal information about you, which can sometimes be sensitive or very personal (religious or philosophical beliefs, sexual orientation, political opinions, state of health, etc.)',
                    )}
                  </S.copy.S>
                  <S.copy.S>
                    {t(
                      'Once your comment is published, this information will be public and available to all. We invite you to be careful in writing your comments, to protect your privacy',
                    )}
                  </S.copy.S>
                </>
              )}
            </Form>
          </Wrapper>
        )
      }}
    </formik.Formik>
  )

  async function submit(values: CommentFormValue, helpers: formik.FormikHelpers<CommentFormValue>): Promise<void> {
    await onSubmit(values)
    textareaResetRef.current && textareaResetRef.current()
    helpers.resetForm()
  }

  function validate(values: CommentFormValue): Promise<formik.FormikErrors<CommentFormValue>> {
    const validation = schema.validate(values)
    return validation.then(
      () => ({}),
      (err) => {
        // send flash message
        return formik.yupToFormErrors(err)
      },
    )
  }
}

const PrivacyToggle = styled(ds.LinkAsButton)`
  padding: 0;
`

const Form = styled.form`
  ${S.button.Button}, ${ds.LinkAsButton} {
    margin: -10px 0 4px;
  }

  ${S.copy.S} {
    margin-bottom: 14px;
    color: ${COLORS.GREY_SHADE_2};
  }

  ${ds.TextAreaField} {
    ${S.field.Status} {
      align-items: baseline;
      justify-content: flex-end;
    }
    ${S.input.Count} {
      margin-left: 10px;
    }
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: stretch;

  ${Form} {
    flex: 1;
    margin-left: 20px;
  }
`
